import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../utils/cn';
const OFFSET = 8;
function calculatePosition(triggerRect, tooltipRect, placement) {
    let top = 0;
    let left = 0;
    switch (placement) {
        case 'top':
            top = triggerRect.top - tooltipRect.height - OFFSET;
            left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
            break;
        case 'bottom':
            top = triggerRect.bottom + OFFSET;
            left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
            break;
        case 'left':
            top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
            left = triggerRect.left - tooltipRect.width - OFFSET;
            break;
        case 'right':
            top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
            left = triggerRect.right + OFFSET;
            break;
    }
    // Clamp to viewport
    top = Math.max(4, Math.min(top, window.innerHeight - tooltipRect.height - 4));
    left = Math.max(4, Math.min(left, window.innerWidth - tooltipRect.width - 4));
    return { top, left };
}
export const Tooltip = ({ content, children, placement = 'top', delay = 200, hideDelay = 0, disabled = false, className, }) => {
    const [visible, setVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);
    const showTimeoutRef = useRef();
    const hideTimeoutRef = useRef();
    const show = useCallback(() => {
        if (disabled)
            return;
        clearTimeout(hideTimeoutRef.current);
        showTimeoutRef.current = setTimeout(() => {
            setVisible(true);
        }, delay);
    }, [delay, disabled]);
    const hide = useCallback(() => {
        clearTimeout(showTimeoutRef.current);
        hideTimeoutRef.current = setTimeout(() => {
            setVisible(false);
        }, hideDelay);
    }, [hideDelay]);
    // Update position when visible
    useEffect(() => {
        if (visible && triggerRef.current && tooltipRef.current) {
            const triggerRect = triggerRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            setPosition(calculatePosition(triggerRect, tooltipRect, placement));
        }
    }, [visible, placement]);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearTimeout(showTimeoutRef.current);
            clearTimeout(hideTimeoutRef.current);
        };
    }, []);
    // Generate a unique ID for ARIA
    const tooltipId = useRef(`tooltip-${Math.random().toString(36).substring(2, 9)}`);
    // Clone the child to attach event handlers and ref
    const trigger = React.cloneElement(children, {
        ref: (node) => {
            triggerRef.current = node;
            // Forward the ref if the child has one
            const childRef = children.ref;
            if (typeof childRef === 'function') {
                childRef(node);
            }
            else if (childRef && typeof childRef === 'object') {
                childRef.current = node;
            }
        },
        onMouseEnter: (e) => {
            show();
            children.props.onMouseEnter?.(e);
        },
        onMouseLeave: (e) => {
            hide();
            children.props.onMouseLeave?.(e);
        },
        onFocus: (e) => {
            show();
            children.props.onFocus?.(e);
        },
        onBlur: (e) => {
            hide();
            children.props.onBlur?.(e);
        },
        'aria-describedby': visible ? tooltipId.current : undefined,
    });
    return (_jsxs(_Fragment, { children: [trigger, visible &&
                createPortal(_jsx("div", { ref: tooltipRef, id: tooltipId.current, role: "tooltip", className: cn('fixed z-tooltip px-2.5 py-1.5 text-xs font-medium rounded-md shadow-lg pointer-events-none', 'bg-dark-900 text-white dark:bg-dark-100 dark:text-dark-900', 'animate-fade-in', className), style: { top: position.top, left: position.left }, children: content }), document.body)] }));
};
Tooltip.displayName = 'Tooltip';
//# sourceMappingURL=Tooltip.js.map